/**
 * CONTACT-MGMT (CM-6) — alpha-3 country control for the contact form.
 *
 * CM-D17: `contacts.country_code` is ISO 3166-1 **alpha-3** (a deliberate
 * divergence from the assets/sites alpha-2 convention). Rather than fork the
 * shared `CountryAutocomplete` (which emits alpha-2), this is a thin adapter:
 * it converts the alpha-3 model value to alpha-2 for display and converts the
 * alpha-2 selection back to alpha-3 on change via the
 * `iso3166` `toAlpha2` / `toAlpha3` helpers (single source of truth — the same
 * wind-context subset both lists are derived from). The underlying column /
 * contract are unchanged.
 */
import { type ReactElement } from "react";
import { CountryAutocomplete } from "../../sites/shared/CountryAutocomplete.js";
import { toAlpha2, toAlpha3 } from "../../sites/shared/iso3166.js";

export interface ContactCountryAutocompleteProps {
  /** Alpha-3 country code (or null). */
  value: string | null;
  /** Emits an alpha-3 code (or null when cleared). */
  onChange: (next: string | null) => void;
  required?: boolean;
  errorText?: string | null;
  readOnly?: boolean;
  label?: string;
}

export function ContactCountryAutocomplete({
  value,
  onChange,
  required = false,
  errorText = null,
  readOnly = false,
  label = "Country",
}: ContactCountryAutocompleteProps): ReactElement {
  // Model is alpha-3; the inner control works in alpha-2.
  const alpha2Value = value ? toAlpha2(value) : null;
  return (
    <CountryAutocomplete
      value={alpha2Value}
      onChange={(nextAlpha2) => {
        if (!nextAlpha2) {
          onChange(null);
          return;
        }
        // Resolve to alpha-3 (CM-D17). When the wind-context subset has no
        // alpha-3 mapping, fall back to the uppercased input — still valid
        // per the contract (minLength 2) and never silently dropped.
        onChange(toAlpha3(nextAlpha2) ?? nextAlpha2.toUpperCase());
      }}
      required={required}
      errorText={errorText}
      readOnly={readOnly}
      label={label}
    />
  );
}
